import { useMemo, useCallback } from "react";
import { useTableScrollReset } from "@/hooks/useTableScrollReset";
import { useUpdateDomainField } from "@/hooks/useUpdateDomainField";
import { Table, TableBody, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { COLUMNS, type Column } from "@/lib/table-columns";
import { useTableSort } from "./useTableSort";
import { sortDomains } from "./tableSortUtils";
import { buildHeaderGroups } from "./tableHeaderUtils";
import { TableHeaderCell } from "./TableHeaderCell";
import { TableRow as DomainTableRow } from "./TableRow";
import type { EditableCellValue } from "../EditableCell";
import type { DomainsTableProps } from "./types";

export function DomainsTable({
  domains,
  className,
  visibleColumns,
  columnOrder,
  scrollResetKey,
}: DomainsTableProps) {
  const { mutateAsync: updateDomain } = useUpdateDomainField();
  const { sortColumn, sortDirection, handleSort } = useTableSort();
  const { rootRef, scrollContainerRef } = useTableScrollReset(scrollResetKey);

  const handleUpdateDomain = useCallback(
    async (id: string, column: string, value: EditableCellValue) => {
      await updateDomain({ id, column, value });
    },
    [updateDomain],
  );

  const activeColumns = useMemo(() => {
    return columnOrder
      .map((key) => COLUMNS.find((col) => col.key === key))
      .filter(
        (col): col is Column =>
          col !== undefined && visibleColumns[col.key] !== false,
      );
  }, [visibleColumns, columnOrder]);

  const sortedDomains = useMemo(
    () => sortDomains(domains, sortColumn, sortDirection),
    [domains, sortColumn, sortDirection],
  );

  const headerGroups = useMemo(
    () => buildHeaderGroups(activeColumns),
    [activeColumns],
  );

  return (
    <div
      ref={rootRef}
      className={cn(
        "relative flex scroll-mt-20 flex-col rounded-lg border border-border bg-card",
        "h-full max-w-full",
        className,
      )}
    >
      <div
        ref={scrollContainerRef}
        className={cn(
          "overflow-auto w-full",
          sortedDomains.length > 0 ? "flex-1" : "flex-none",
        )}
      >
        <Table>
          <TableHeader className="sticky top-0 z-10 border-b border-border bg-muted/50">
            <TableRow>
              {headerGroups.map((group, index) => (
                <TableHeaderCell
                  key={`group-${index}-${group.name}`}
                  label={group.isGroup ? group.name : group.label || ""}
                  columnKey={group.name}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  isGroup={group.isGroup}
                  colSpan={group.isGroup ? group.colSpan : undefined}
                  rowSpan={!group.isGroup ? 2 : undefined}
                />
              ))}
            </TableRow>

            <TableRow>
              {activeColumns.map((col) => {
                if (!col.group) return null;

                return (
                  <TableHeaderCell
                    key={col.key}
                    label={col.label}
                    columnKey={col.key}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                );
              })}
            </TableRow>
          </TableHeader>

          {sortedDomains.length > 0 && (
            <TableBody>
              {sortedDomains.map((domain) => (
                <DomainTableRow
                  key={domain.id}
                  domain={domain}
                  columns={activeColumns}
                  onUpdateDomain={handleUpdateDomain}
                />
              ))}
            </TableBody>
          )}
        </Table>
      </div>

      {sortedDomains.length === 0 && (
        <div className="flex-1 flex items-center justify-center min-h-[400px]">
          <span className="text-lg font-medium text-muted-foreground">
            No data available
          </span>
        </div>
      )}
    </div>
  );
}
